import React, { Fragment, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp, faEnvelope, faKey, faLock, faPhoneAlt, faSave, faUser, faUserTag } from "@fortawesome/free-solid-svg-icons";
import util from "../../utils/miniUtils";
import { ADD_USER, EDIT_USER, ADMIN_USER, EMPLOYEE_USER, MECHANIC_USER, PROVIDER_USER } from "../../utils/constants";
import { PHONE_DIGITS, PHONE_PLACEHOLDER, formatPhone, maskPhone, toPhoneDigits } from "./phone";
import "./UserProfile.css";

const FORM_ID = "user-profile-form";

const ROLE_OPTIONS = [
    { value: String( ADMIN_USER ), label: "Admin" },
    { value: String( EMPLOYEE_USER ), label: "Empleado" },
    { value: String( MECHANIC_USER ), label: "Mecánico" },
    { value: String( PROVIDER_USER ), label: "Proveedor" },
];

const validationSchema = Yup.object().shape( {
    firstName: Yup.string( )
        .required( "El nombre es requerido" )
        .min( 2, "Mínimo 2 caracteres" )
        .max( 50, "Máximo 50 caracteres" ),
    lastName: Yup.string( )
        .required( "El apellido es requerido" )
        .min( 2, "Mínimo 2 caracteres" )
        .max( 50, "Máximo 50 caracteres" ),
    email: Yup.string( )
        .required( "El email es requerido" )
        .email( "Email inválido" ),
    phone: Yup.string( )
        .required( "El teléfono es requerido" )
        .test( "phone-digits", `El teléfono debe tener ${PHONE_DIGITS} dígitos`, ( value ) => toPhoneDigits( value ).length === PHONE_DIGITS ),
    role: Yup.string( )
        .required( "El rol es requerido" )
        .oneOf( ROLE_OPTIONS.map( ( role ) => role.value ), "Rol inválido" )
} );

const passwordValidationSchema = Yup.object().shape( {
    password: Yup.string( )
        .required( "La contraseña es requerida" )
        .min( 8, "Mínimo 8 caracteres" )
        .max( 20, "Máximo 20 caracteres" ),
    passwordConfirmation: Yup.string( )
        .required( "Confirmar contraseña es requerido" )
        .oneOf( [ Yup.ref( "password" ), null ], "Las contraseñas no son iguales" )
} );

const getInitials = ( firstName, lastName ) => `${firstName.trim().charAt( 0 )}${lastName.trim().charAt( 0 )}`.toUpperCase();

const formatDate = ( date ) => date
    ? new Date( date ).toLocaleDateString( "es-MX", { day: "numeric", month: "short", year: "numeric" } )
    : "Sin registros";

export default function UserProfile( { user_id, show, handleClose, reloadTable, action = ADD_USER } ) {

    const [changePassword, setChangePassword] = useState( {
        password: "",
        passwordConfirmation: "",
        show: false,
    } );
    const [userStats, setUserStats] = useState( {
        totalIngresados: 0,
        totalRetirados: 0,
        ultimoIngresoFecha: null,
        ultimoRetiroFecha: null
    } );
    const [loading, setLoading] = useState( false );
    const startLoading = () => setLoading( true );
    const stopLoading = () => setLoading( false );

    const formik = useFormik( {
        initialValues: {
            firstName: "",
            lastName: "",
            phone: "",
            email: "",
            role: String( EMPLOYEE_USER )
        },
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit: saveUserData
    } );

    useEffect( () => {
        if ( action === EDIT_USER ) {
            getUsersDetail();
            getUserStats();
        }
    }, [ action, user_id ] );

    async function getUsersDetail() {
        try {
            startLoading();
            const request = await util.axios.get( `users/detail/${user_id}` );
            let { error, msg, data } = request.data;
            if ( error ) throw msg;
            stopLoading();
            formik.setValues( {
                firstName: data.firstName || "",
                lastName: data.lastName || "",
                phone: formatPhone( data.phone ) || "",
                email: data.email || "",
                role: data.role ? String( data.role ) : String( EMPLOYEE_USER )
            } );
        } catch ( e ) {
            stopLoading();
            util.handleError( e );
        }
    }

    async function getUserStats() {
        try {
            const request = await util.axios.get( `users/${user_id}/stats` );
            let { error, data } = request.data;
            if ( error ) throw error;
            setUserStats( data );
        } catch ( e ) {
            util.handleError( e );
        }
    }

    async function saveUserData( values ) {
        try {
            startLoading();
            const userData = {
                ...values,
                phone: toPhoneDigits( values.phone ),
                role: String( values.role )
            };
            let request;
            if ( action === ADD_USER ) {
                request = await util.axios.post( `users/`, userData );
            } else {
                request = await util.axios.put( `users/${user_id}`, userData );
            }

            let { error, msg, data } = request.data;
            if ( error ) throw msg;
            stopLoading();

            // Al crear, el usuario recibe por correo el link para definir su contraseña
            if ( action === ADD_USER && !data.emailSent ) {
                util.toast().warning( msg );
            } else {
                util.toast().success( msg );
            }
            reloadTable();
            handleClose();
        } catch ( e ) {
            stopLoading();
            util.handleError( e );
        }
    }

    async function saveNewPassword() {
        try {
            startLoading();
            await passwordValidationSchema.validate( {
                password: changePassword.password,
                passwordConfirmation: changePassword.passwordConfirmation
            } );
            let request = await util.axios.put( `users/change_password/${user_id}`, changePassword );
            let { error, msg } = request.data;
            if ( error ) throw msg;
            stopLoading();
            util.toast().success( msg );
            reloadTable();
            handleClose();
        } catch ( e ) {
            stopLoading();
            util.handleError( e );
        }
    }

    const handleChangePassword = ( e ) => {
        const { name, value } = e.target;
        setChangePassword( { ...changePassword, [name]: value } );
    };

    const togglePasswordForm = () => {
        setChangePassword( { show: !changePassword.show, password: "", passwordConfirmation: "" } );
    };

    const handlePasswordSubmit = ( e ) => {
        e.preventDefault();
        saveNewPassword();
    };

    const handlePhoneChange = ( e ) => {
        formik.setFieldValue( "phone", maskPhone( e.target.value ) );
    };

    const getFieldError = ( fieldName ) => {
        return formik.touched[fieldName] && formik.errors[fieldName] ? formik.errors[fieldName] : null;
    };

    const isEdit = action === EDIT_USER;
    const { firstName, lastName, email } = formik.values;
    const initials = getInitials( firstName, lastName );
    const fullName = `${firstName} ${lastName}`.trim();

    return (
        <Fragment>
            <Modal show={show} onHide={handleClose} size={isEdit ? "lg" : undefined} centered>
                <Modal.Header closeButton>
                    <div className="d-flex align-items-center gap-3">
                        <div className="user-profile-avatar" aria-hidden="true">
                            {initials || <FontAwesomeIcon icon={faUser} />}
                        </div>
                        <div>
                            <Modal.Title as="h5" className="mb-0">
                                {isEdit ? fullName || "Perfil de usuario" : "Nuevo usuario"}
                            </Modal.Title>
                            <div className="user-profile-subtitle">
                                {isEdit ? email : "Recibirá un correo para definir su contraseña"}
                            </div>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body>
                    <div className="row g-4">
                        <div className={isEdit ? "col-lg-7" : "col-12"}>
                            <h6 className="user-profile-section-title">Datos personales</h6>
                            <form id={FORM_ID} onSubmit={formik.handleSubmit} noValidate className="row g-3">
                                <Field
                                    className="col-sm-6"
                                    label="Nombre"
                                    name="firstName"
                                    icon={faUser}
                                    autoComplete="off"
                                    value={formik.values.firstName}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={getFieldError( "firstName" )}
                                />
                                <Field
                                    className="col-sm-6"
                                    label="Apellido"
                                    name="lastName"
                                    icon={faUser}
                                    autoComplete="off"
                                    value={formik.values.lastName}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={getFieldError( "lastName" )}
                                />
                                <Field
                                    className="col-12"
                                    label="Correo"
                                    name="email"
                                    type="email"
                                    icon={isEdit ? faLock : faEnvelope}
                                    placeholder="nombre@correo.com"
                                    autoComplete="off"
                                    readOnly={isEdit}
                                    hint={isEdit ? "El correo no se puede modificar." : null}
                                    value={formik.values.email}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={getFieldError( "email" )}
                                />
                                <Field
                                    className="col-sm-6"
                                    label="Teléfono"
                                    name="phone"
                                    type="tel"
                                    inputMode="tel"
                                    icon={faPhoneAlt}
                                    placeholder={PHONE_PLACEHOLDER}
                                    autoComplete="off"
                                    value={formik.values.phone}
                                    onChange={handlePhoneChange}
                                    onBlur={formik.handleBlur}
                                    error={getFieldError( "phone" )}
                                />
                                <Field
                                    as="select"
                                    className="col-sm-6"
                                    label="Rol"
                                    name="role"
                                    icon={faUserTag}
                                    value={formik.values.role}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={getFieldError( "role" )}
                                >
                                    {ROLE_OPTIONS.map( ( role ) => (
                                        <option key={role.value} value={role.value}>{role.label}</option>
                                    ) )}
                                </Field>
                            </form>
                        </div>

                        {isEdit && (
                            <div className="col-lg-5">
                                <h6 className="user-profile-section-title">Actividad</h6>
                                <div className="user-profile-stats">
                                    <StatTile
                                        tone="in"
                                        icon={faArrowDown}
                                        label="Ingresados"
                                        value={userStats.totalIngresados}
                                        date={formatDate( userStats.ultimoIngresoFecha )}
                                    />
                                    <StatTile
                                        tone="out"
                                        icon={faArrowUp}
                                        label="Retirados"
                                        value={userStats.totalRetirados}
                                        date={formatDate( userStats.ultimoRetiroFecha )}
                                    />
                                </div>

                                <h6 className="user-profile-section-title mt-4">Seguridad</h6>
                                {changePassword.show ? (
                                    <form onSubmit={handlePasswordSubmit} noValidate className="user-profile-password">
                                        <Field
                                            className="mb-2"
                                            label="Nueva contraseña"
                                            name="password"
                                            type="password"
                                            icon={faLock}
                                            placeholder="Mínimo 8 caracteres"
                                            autoComplete="new-password"
                                            value={changePassword.password}
                                            onChange={handleChangePassword}
                                        />
                                        <Field
                                            className="mb-3"
                                            label="Confirmar contraseña"
                                            name="passwordConfirmation"
                                            type="password"
                                            icon={faLock}
                                            autoComplete="new-password"
                                            value={changePassword.passwordConfirmation}
                                            onChange={handleChangePassword}
                                        />
                                        <div className="d-flex justify-content-end gap-2">
                                            <button type="button" className="btn btn-sm btn-light" onClick={togglePasswordForm}>
                                                Cancelar
                                            </button>
                                            <button type="submit" className="btn btn-sm btn-success">
                                                Actualizar contraseña
                                            </button>
                                        </div>
                                    </form>
                                ) : (
                                    <button type="button" className="btn btn-sm btn-outline-secondary w-100" onClick={togglePasswordForm}>
                                        <FontAwesomeIcon icon={faKey} className="me-2" />
                                        Cambiar contraseña
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-sm btn-light" onClick={handleClose}>
                        Cancelar
                    </button>
                    <button type="submit" form={FORM_ID} className="btn btn-sm btn-primary" disabled={loading}>
                        <FontAwesomeIcon icon={faSave} className="me-2" />
                        {isEdit ? "Guardar cambios" : "Crear usuario"}
                    </button>
                </Modal.Footer>
            </Modal>

            {util.LOADING_SCREEN( loading )}
        </Fragment>
    );
}


function Field( { label, name, icon, error = null, hint = null, as: Control = "input", className = "", children, ...controlProps } ) {
    const id = `user-profile-${name}`;
    const controlClass = Control === "select" ? "form-select" : "form-control";

    return (
        <div className={className}>
            <label htmlFor={id} className="form-label user-profile-label">{label}</label>
            <div className="input-group has-validation">
                <span className="input-group-text user-profile-icon">
                    <FontAwesomeIcon icon={icon} fixedWidth />
                </span>
                <Control
                    id={id}
                    name={name}
                    className={`${controlClass}${error ? " is-invalid" : ""}`}
                    aria-invalid={!!error}
                    {...controlProps}
                >
                    {children}
                </Control>
                {error && <div className="invalid-feedback">{error}</div>}
            </div>
            {hint && <div className="form-text">{hint}</div>}
        </div>
    );
}

function StatTile( { tone, icon, label, value, date } ) {
    return (
        <div className={`user-profile-stat user-profile-stat--${tone}`}>
            <div className="d-flex align-items-center gap-2">
                <span className="user-profile-stat__icon">
                    <FontAwesomeIcon icon={icon} />
                </span>
                <span className="user-profile-stat__label">{label}</span>
            </div>
            <div className="user-profile-stat__value">{value}</div>
            <div className="user-profile-stat__date">Último: {date}</div>
        </div>
    );
}
